import React, { useState, useEffect, useCallback } from "react";
import { FlaskConical, Plus, Trash2, QrCode, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_METIERS = [
  "plombier", "electricien", "serrurier", "chauffagiste", "peintre",
  "menuisier", "paysagiste", "renovation", "carreleur", "facadier",
  "couvreur", "ferblantier", "vitrier", "macon", "platrier", "photovoltaique",
];

export const ABTestSection = () => {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ metier: "", variant_id: "", hook: "", subtitle: "" });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config/ab-hooks`);
      setHooks(res.data.hooks || []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addHook = async () => {
    if (!form.metier || !form.variant_id || !form.hook) return;
    try {
      await axios.post(`${API}/admin/config/ab-hooks`, form);
      setForm({ metier: "", variant_id: "", hook: "", subtitle: "" });
      load();
    } catch { /* */ }
  };

  const removeHook = async (metier, variant_id) => {
    try {
      await axios.delete(`${API}/admin/config/ab-hooks/${encodeURIComponent(metier)}/${encodeURIComponent(variant_id)}`);
      load();
    } catch { /* */ }
  };

  const downloadQR = async (metier) => {
    try {
      const res = await axios.get(`${API}/admin/config/qrcode/${metier}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr_diagnostic_${metier}.png`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* */ }
  };

  // Group hooks by metier
  const grouped = hooks.reduce((acc, h) => {
    if (!acc[h.metier]) acc[h.metier] = [];
    acc[h.metier].push(h);
    return acc;
  }, {});

  return (
    <div className="bg-card rounded-lg border p-6" data-testid="ab-test-section">
      <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
        <FlaskConical className="w-5 h-5 text-primary" /> A/B Test Hooks + QR Codes
      </h2>
      <p className="text-xs text-muted-foreground mb-5">Testez différents hooks par métier. Le frontend choisit aléatoirement une variante et suit les conversions.</p>

      {/* QR Codes quick download */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg">
        <p className="text-sm font-semibold mb-3 flex items-center gap-2"><QrCode className="w-4 h-4" /> QR Codes Diagnostic</p>
        <div className="flex flex-wrap gap-2">
          {ALL_METIERS.map((m) => (
            <Button key={m} variant="outline" size="sm" onClick={() => downloadQR(m)} className="text-xs">
              <Download className="w-3 h-3 mr-1" /> {m}
            </Button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Chaque QR pointe vers /diagnostic?utm_source=qr&utm_campaign={"{metier}"}</p>
      </div>

      {/* Existing hooks with stats */}
      {Object.keys(grouped).length > 0 && (
        <div className="space-y-4 mb-6">
          {Object.entries(grouped).map(([metier, variants]) => (
            <div key={metier} className="border rounded-lg p-4">
              <p className="text-sm font-bold mb-2 capitalize">{metier}</p>
              <div className="space-y-2">
                {variants.map((v) => {
                  const rate = v.views > 0 ? ((v.conversions / v.views) * 100).toFixed(1) : "—";
                  return (
                    <div key={v.variant_id} className="flex items-center justify-between gap-3 bg-muted/20 rounded px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold truncate">[{v.variant_id}] {v.hook}</p>
                        <p className="text-[10px] text-muted-foreground">{v.subtitle}</p>
                      </div>
                      <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                        <span>{v.views || 0} vues</span>
                        <span>{v.conversions || 0} conv.</span>
                        <span className={`font-bold ${rate !== "—" && parseFloat(rate) > 5 ? "text-green-600" : "text-muted-foreground"}`}>{rate}%</span>
                        <button onClick={() => removeHook(metier, v.variant_id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new hook */}
      <div className="border-t pt-4">
        <p className="text-sm font-semibold mb-3">Ajouter une variante</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
          <div>
            <label className="text-xs font-medium">Métier</label>
            <select value={form.metier} onChange={(e) => setForm((p) => ({ ...p, metier: e.target.value }))} className="w-full border rounded px-2 py-1.5 text-xs">
              <option value="">Choisir...</option>
              {ALL_METIERS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium">ID Variante</label>
            <Input value={form.variant_id} onChange={(e) => setForm((p) => ({ ...p, variant_id: e.target.value }))} placeholder="A, B, C..." className="h-8 text-xs" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium">Hook</label>
            <Input value={form.hook} onChange={(e) => setForm((p) => ({ ...p, hook: e.target.value }))} placeholder="On vous trouve des..." className="h-8 text-xs" />
          </div>
          <div className="sm:col-span-3">
            <label className="text-xs font-medium">Sous-titre</label>
            <Input value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))} placeholder="Sous-titre optionnel..." className="h-8 text-xs" />
          </div>
          <Button size="sm" onClick={addHook} disabled={!form.metier || !form.variant_id || !form.hook}>
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>
    </div>
  );
};
